<p-dialog
  [(visible)]="visible"
  [modal]="true"
  [dismissableMask]="false"
  styleClass="generic-modal-container create-rca-dialog"
  (onHide)="close()"
  [draggable]="false"
  [resizable]="false"
  [closable]="true"
>
  <ng-template pTemplate="header">
    <div class="modal-header">
      <h2>Regulatory Change Assessment Details</h2>
    </div>
  </ng-template>

  <div class="modal-body">
    <form [formGroup]="rcaForm" autocomplete="off">
      <div class="form-grid-container" [class.has-other-design-docs]="showOtherDesignDocsField">
        <!-- Column 1 & 2 Fields (Left Side - 6 Rows) -->
        <!-- Row 1 -->
        <div class="form-group">
          <label for="title">Title <span class="required">*</span></label>
          <input
            id="title"
            type="text"
            pInputText
            placeholder="Enter Title"
            formControlName="title"
            [readonly]="rcaForm.disabled"
          />
        </div>

        <div class="form-group">
          <label for="region">Region <span class="required">*</span></label>
          <!-- Display selected regions when form is disabled -->
          <div *ngIf="rcaForm.disabled" class="readonly-field-display">
            <span class="readonly-chip" *ngFor="let region of rcaForm.get('region')?.value" 
                  [pTooltip]="shouldShowTooltip(region?.name) ? region?.name : ''" 
                  tooltipPosition="top">
              {{ region?.name }}
            </span>
          </div>
          <!-- Show multiselect when form is enabled -->
          <p-multiSelect
            *ngIf="!rcaForm.disabled"
            id="region"
            [options]="regions"
            optionLabel="name"
            [filter]="true"
            formControlName="region"
            placeholder="Select Region(s)"
            [appendTo]="'body'"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['region'].invalid }"
            display="chip"
            [maxSelectedLabels]="3"
            selectedItemsLabel="{0} regions selected"
          ></p-multiSelect>
        </div>
        <div class="form-group">
          <label for="usMedDeviceClass">US Med Device Class <span class="required">*</span></label>
          <p-dropdown
            id="usMedDeviceClass"
            [options]="usMedDeviceOptions"
            optionLabel="name"
            [filter]="true"
            formControlName="usMedDeviceClass"
            placeholder="Select US Med Device Class"
            [appendTo]="'body'"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['usMedDeviceClass'].invalid }"
            [disabled]="rcaForm.disabled"
          ></p-dropdown>
        </div>

        <!-- Row 2 -->
        <div class="form-group">
          <label for="country">Country <span class="required">*</span></label>
          <!-- Display selected countries when form is disabled -->
          <div *ngIf="rcaForm.disabled" class="readonly-field-display">
            <span class="readonly-chip" *ngFor="let country of rcaForm.get('country')?.value" 
                  [pTooltip]="shouldShowTooltip(country?.name) ? country?.name : ''" 
                  tooltipPosition="top">
              {{ country?.name }}
            </span>
          </div>
          <!-- Show multiselect when form is enabled -->
          <p-multiSelect
            *ngIf="!rcaForm.disabled"
            id="country"
            [options]="countries"
            optionLabel="name"
            [filter]="true"
            formControlName="country"
            placeholder="Select Countries"
            [appendTo]="'body'"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['country'].invalid }"
            display="chip"
            [maxSelectedLabels]="3"
            selectedItemsLabel="{0} countries selected"
          ></p-multiSelect>
        </div>
        <div class="form-group">
          <label for="usClassification">US Classification <span class="required">*</span></label>
          <p-dropdown
            id="usClassification"
            [options]="usClassifications"
            optionLabel="name"
            [filter]="true"
            formControlName="usClassification"
            placeholder="Select US Classification"
            [appendTo]="'body'"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['usClassification'].invalid }"
            [disabled]="rcaForm.disabled"
          ></p-dropdown>
        </div>

        <!-- Row 3 -->
        <div class="form-group">
          <label for="ecrnEcnNumber">ECRN/ECN Number (optional)</label>
          <input
            id="ecrnEcnNumber"
            type="text"
            pInputText
            placeholder="Enter ECRN/ECN Number"
            formControlName="ecrnEcnNumber"
            [readonly]="rcaForm.disabled"
          />
        </div>
        <div class="form-group">
          <label for="euMedDeviceClass">EU Med Device Class <span class="required">*</span></label>
          <p-dropdown
            id="euMedDeviceClass"
            [options]="euMedDeviceOptions"
            optionLabel="name"
            [filter]="true"
            formControlName="euMedDeviceClass"
            placeholder="Select EU Med Device Class"
            [appendTo]="'body'"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['euMedDeviceClass'].invalid }"
            [disabled]="rcaForm.disabled"
          ></p-dropdown>
        </div>

        <!-- Row 4 -->
        <div class="form-group">
          <label for="typeOfChange">Type of Change <span class="required">*</span></label>
          <!-- Display selected types when form is disabled -->
          <div *ngIf="rcaForm.disabled" class="readonly-field-display">
            <span class="readonly-chip" *ngFor="let type of rcaForm.get('typeOfChange')?.value" 
                  [pTooltip]="shouldShowTooltip(type?.name) ? type?.name : ''" 
                  tooltipPosition="top">
              {{ type?.name }}
            </span>
          </div>
          <!-- Show multiselect when form is enabled -->
          <p-multiSelect
            *ngIf="!rcaForm.disabled"
            id="typeOfChange"
            [options]="typeOfChanges"
            optionLabel="name"
            [filter]="true"
            formControlName="typeOfChange"
            placeholder="Select Types of Change"
            [appendTo]="'body'"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['typeOfChange'].invalid }"
            display="chip"
            [maxSelectedLabels]="3"
            selectedItemsLabel="{0} types selected"
          ></p-multiSelect>
        </div>
        <div class="form-group">
          <label for="euClassification">EU Classification <span class="required">*</span></label>
          <p-dropdown
            id="euClassification"
            [options]="euClassifications"
            optionLabel="name"
            [filter]="true"
            formControlName="euClassification"
            placeholder="Select EU Classification"
            [appendTo]="'body'"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['euClassification'].invalid }"
            [disabled]="rcaForm.disabled"
          ></p-dropdown>
        </div>

        <!-- Row 5 -->
        <div class="form-group">
          <label for="productFamily">Product Family <span class="required">*</span></label>
          <!-- Display selected product families when form is disabled -->
          <div *ngIf="rcaForm.disabled" class="readonly-field-display">
            <span class="readonly-chip" *ngFor="let family of rcaForm.get('productFamily')?.value" 
                  [pTooltip]="shouldShowTooltip(family?.name) ? family?.name : ''" 
                  tooltipPosition="top">
              {{ family?.name }}
            </span>
          </div>
          <!-- Show multiselect when form is enabled -->
          <p-multiSelect
            *ngIf="!rcaForm.disabled"
            id="productFamily"
            [options]="productFamilies"
            optionLabel="name"
            [filter]="true"
            formControlName="productFamily"
            placeholder="Select Product Families"
            [appendTo]="'body'"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['productFamily'].invalid }"
            display="chip"
            [maxSelectedLabels]="3"
            selectedItemsLabel="{0} families selected"
          ></p-multiSelect>
        </div>
        <div class="form-group">
          <label for="issuedDate">Issued Date <span class="required">*</span></label>
          <p-calendar
            id="issuedDate"
            formControlName="issuedDate"
            [showIcon]="true"
            dateFormat="yy-mm-dd"
            placeholder="Select Issued Date"
            [appendTo]="'body'"
            [readonlyInput]="true"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['issuedDate'].invalid }"
            [disabled]="rcaForm.disabled"
          ></p-calendar>
        </div>

        <!-- Row 6 -->
        <div class="form-group">
          <label for="catalogueNumber">Catalogue Number <span class="required">*</span></label>
          <input
            id="catalogueNumber"
            type="text"
            pInputText
            placeholder="Enter Catalogue Number"
            formControlName="catalogueNumber"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['catalogueNumber'].invalid }"
            [readonly]="rcaForm.disabled"
          />
        </div>
        <div class="form-group">
          <label for="dueDate">Due Date <span class="required">*</span></label>
          <p-calendar
            id="dueDate"
            formControlName="dueDate"
            [showIcon]="true"
            dateFormat="yy-mm-dd"
            placeholder="Select Due Date"
            [appendTo]="'body'"
            [readonlyInput]="true"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['dueDate'].invalid }"
            [disabled]="rcaForm.disabled"
          ></p-calendar>
        </div>

        <!-- Column 3 Fields (Right Side - Spanning) -->
        <div class="form-group product-design-history-group">
          <label for="productDesignHistoryDocumentation">Product Design History Documentation <span class="required">*</span></label>
          <!-- Display selected documents when form is disabled -->
          <div *ngIf="rcaForm.disabled" class="readonly-field-display">
            <span class="readonly-chip" *ngFor="let doc of rcaForm.get('productDesignHistoryDocumentation')?.value" 
                  [pTooltip]="shouldShowTooltip(doc?.name, 40) ? doc?.name : ''" 
                  tooltipPosition="top">
              {{ doc?.name }}
            </span>
          </div>
          <!-- Show multiselect when form is enabled -->
          <p-multiSelect
            *ngIf="!rcaForm.disabled"
            id="productDesignHistoryDocumentation"
            [options]="productDesignHistoryOptions"
            optionLabel="name"
            [filter]="true"
            formControlName="productDesignHistoryDocumentation"
            placeholder="Select Product Design History Documentation"
            [appendTo]="'body'"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['productDesignHistoryDocumentation'].invalid }"
            display="chip"
            [maxSelectedLabels]="3"
            selectedItemsLabel="{0} documents selected"
          ></p-multiSelect>
        </div>

        <!-- Other Design Docs field - shown when "Other(s)" is selected -->
        <div class="form-group other-design-docs-group" *ngIf="showOtherDesignDocsField">
          <label for="otherDesignDocs">Other Design Docs</label>
          <input
            id="otherDesignDocs"
            type="text"
            pInputText
            placeholder="Custom validation documentation and internal test reports"
            formControlName="otherDesignDocs"
            [readonly]="rcaForm.disabled"
          />
        </div>

        <div class="form-group change-description-group">
          <label for="changeDescription">Change Description <span class="required">*</span></label>
          <!-- Display HTML content when form is disabled (read-only mode); maximize opens full dialog -->
          <div *ngIf="rcaForm.disabled" class="readonly-maximize-field">
            <ng-container
              *ngTemplateOutlet="readonlyMaximizeToolbar; context: { field: 'changeDescription', ariaLabel: 'Open change description in maximized view' }"
            ></ng-container>
            <div
              class="change-description-display"
              role="textbox"
              aria-readonly="true"
              [innerHTML]="rcaForm.get('changeDescription')?.value"
            ></div>
          </div>
          <!-- Use editor when form is editable -->
          <p-editor
            *ngIf="!rcaForm.disabled"
            id="changeDescription"
            formControlName="changeDescription"
            [style]="{'height':'150px'}"
            placeholder="Enter the description for change assessment"
            [modules]="editorModules"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['changeDescription'].invalid }"
          ></p-editor>
        </div>

        <!-- Column 4 Fields (Right Side - Spanning) -->
        <div class="form-group planned-timeframe-group">
          <label for="plannedTimeframeForImplementation">Planned Timeframe for Implementation <span class="required">*</span></label>
          <input
            id="plannedTimeframeForImplementation"
            type="text"
            pInputText
            placeholder="Enter Planned Timeframe for Implementation"
            formControlName="plannedTimeframeForImplementation"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['plannedTimeframeForImplementation'].invalid }"
            [readonly]="rcaForm.disabled"
          />
        </div>

        <div class="form-group additional-attachments-group">
          <label for="additionalAttachments">Additional Attachments (optional)</label>
          <div class="attachment-upload-container">
            <textarea
              id="additionalAttachments"
              pInputTextarea
              rows="2"
              placeholder="Description about the files uploaded"
              formControlName="additionalAttachmentsDescription"
              class="attachment-description"
              [readonly]="rcaForm.disabled"
            ></textarea>
            <div *ngIf="raAdminUploadedFiles.length > 0" class="selected-files-list">
              <div *ngFor="let file of raAdminUploadedFiles" class="selected-file-display">
                <ng-container *ngIf="getFileStatusInfo(file) as statusInfo">
                  <div class="file-info">
                    <i class="pi pi-file mr-2"></i>
                    <span class="file-name">{{ file.name }}</span>
                    <i
                      class="pi {{ statusInfo.icon }} ml-2 {{ statusInfo.class }}"
                      [pTooltip]="statusInfo.text"
                      *ngIf="statusInfo.text"
                    ></i>
                  </div>
                  <div class="file-actions">
                    <button
                      pButton
                      type="button"
                      icon="pi pi-download"
                      class="p-button-text p-button-sm"
                      (click)="downloadFile(file)"
                      [disabled]="!statusInfo.canDownload"
                      [pTooltip]="statusInfo.canDownload ? 'Download file' : (statusInfo.text || 'File not available for download')"
                    ></button>
                  </div>
                </ng-container>
              </div>
            </div>
          </div>
        </div>
  </div>

  <hr class="section-divider" />

  <!-- New fields from the response form -->
      <div class="device-form-grid-container">
        <!-- Row 1 -->
        <div class="form-group" *ngIf="rcaForm.get('deviceClassification')?.value">
          <label for="deviceClassification">Device classification in country <span class="required">*</span></label>
          <input
            id="deviceClassificationCountry"
            type="text"
            pInputText
            placeholder="Device Classification"
            formControlName="deviceClassification"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['deviceClassification'].invalid }"
            [readonly]="rcaForm.disabled"
          />
        </div>
        <div class="form-group impact-assess-justify-group" *ngIf="rcaForm.get('impactAssessJustify')?.value">
          <label for="impactAssessJustify">Impact assessment and justification <span class="required">*</span></label>
          <div *ngIf="rcaForm.disabled" class="readonly-maximize-field">
            <ng-container
              *ngTemplateOutlet="readonlyMaximizeToolbar; context: { field: 'impactAssessJustify', ariaLabel: 'Open impact assessment and justification in maximized view' }"
            ></ng-container>
            <div
              class="change-description-display impact-assess-plain"
              role="textbox"
              aria-readonly="true"
            >{{ rcaForm.get('impactAssessJustify')?.value }}</div>
          </div>
          <textarea
            *ngIf="!rcaForm.disabled"
            id="impactAssessJustify"
            pInputTextarea
            rows="3"
            placeholder="Impact Assessment"
            formControlName="impactAssessJustify"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['impactAssessJustify'].invalid }"
          ></textarea>
        </div>
        <!-- Row 2 -->
        <div class="form-group" *ngIf="rcaForm.get('impactConclusion')?.value">
          <label for="impactConclusion">Impact Conclusion <span class="required">*</span></label>
          <input
            id="impactConclusion"
            type="text"
            pInputText
            placeholder="Impact Conclusion"
            formControlName="impactConclusion"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['impactConclusion'].invalid }"
            [readonly]="rcaForm.disabled"
          />
        </div>
        <div class="form-group" *ngIf="rcaForm.get('timeSubmissions')?.value">
          <label for="timeSubmissions">Time required for local regulatory to prepare submissions (Days) <span class="required">*</span></label>
          <input
            id="timeSubmissions"
            type="text"
            pInputText
            placeholder="Time submission"
            formControlName="timeSubmissions"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['timeSubmissions'].invalid }"
            [readonly]="rcaForm.disabled"
          />
        </div>
        <!-- Row 3 -->
        
        <div class="form-group" *ngIf="rcaForm.get('timeApprove')?.value">
          <label for="timeApprove">Time required for Health authority to review & approve. (Days) <span class="required">*</span></label>
          <input
            id="timeApprove"
            type="text"
            pInputText
            placeholder="Time Health authority"
            formControlName="timeApprove"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['timeApprove'].invalid }"
            [readonly]="rcaForm.disabled"
          />
        </div>
        <!-- Row 4  -->
        <div class="form-group" *ngIf="rcaForm.get('productAuthority')?.value">
          <label for="productAuthority">Will you accept the modified product/operation manual prior to health authority approval. <span class="required">*</span></label>
          <input
            id="productAuthority"
            type="text"
            pInputText
            placeholder="Product Authority"
            formControlName="productAuthority"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['productAuthority'].invalid }"
            [readonly]="rcaForm.disabled"
          />
        </div>
        <!-- Row 5  -->
        <div class="form-group" *ngIf="rcaForm.get('productNotify')?.value">
          <label for="productNotify">For Product Changes, after notification/Health Authority approval, will you accept both products on the market, (old and new version)? <span class="required">*</span></label>
          <input
            id="productNotify"
            type="text"
            pInputText
            placeholder="Product Change Authority"
            formControlName="productNotify"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['productNotify'].invalid }"
            [readonly]="rcaForm.disabled"
          />
        </div>
        <div class="form-group impact-assess-justify-group" *ngIf="rcaForm.get('timeProductNotify')?.value">
          <label for="timeProductNotify">For how long can you accept both old and new versions of products on the market? (Days) <span class="required">*</span></label>
          <div *ngIf="rcaForm.disabled" class="readonly-maximize-field">
            <ng-container
              *ngTemplateOutlet="readonlyMaximizeToolbar; context: { field: 'timeProductNotify', ariaLabel: 'Open acceptable duration in maximized editor' }"
            ></ng-container>
            <div
              class="change-description-display impact-assess-plain"
              role="textbox"
              aria-readonly="true"
            >{{ rcaForm.get('timeProductNotify')?.value }}</div>
          </div>
          <textarea
            *ngIf="!rcaForm.disabled"
            id="timeProductNotify"
            pInputTextarea
            rows="3"
            placeholder="Impact Assessment"
            formControlName="timeProductNotify"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['timeProductNotify'].invalid }"
          ></textarea>
        </div>
        <div class="form-group" *ngIf="rcaForm.get('supportDocument')?.value">
          <label for="supportDocument">Select the documentation required to support the changes? <span class="required">*</span></label>
          <input
            id="supportDocument"
            type="text"
            pInputText
            placeholder="Support document"
            formControlName="supportDocument"
            [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['supportDocument'].invalid }"
            [readonly]="rcaForm.disabled"
          />
        </div>
        <!-- New: Other Documentation field -->
        <div class="form-group" *ngIf="rcaForm.get('otherDocumentation')?.value">
          <label for="otherDocumentation">Other Documentation</label>
          <input
            id="otherDocumentation"
            type="text"
            pInputText
            placeholder="Other Documentation Details"
            formControlName="otherDocumentation"
            [readonly]="rcaForm.disabled"
          />
        </div>
        <div class="form-group device-additional-attachments-group">
          <label for="deviceAdditionalAttachmentsDesc">Additional Attachments (Response)</label>
          <div class="attachment-upload-container">
            <textarea
              id="deviceAdditionalAttachments"
              pInputTextarea
              rows="2"
              placeholder="Description about the files uploaded"
              formControlName="deviceAdditionalAttachmentsDesc"
              class="attachment-description"
              [readonly]="rcaForm.disabled"
            ></textarea>
            <div *ngIf="responseUploadedFiles.length > 0" class="selected-files-list">
              <div *ngFor="let file of responseUploadedFiles" class="selected-file-display">
                <ng-container *ngIf="getFileStatusInfo(file) as statusInfo">
                  <div class="file-info">
                    <i class="pi pi-file mr-2"></i>
                    <span class="file-name">{{ file.name }}</span>
                    <i
                      class="pi {{ statusInfo.icon }} ml-2 {{ statusInfo.class }}"
                      [pTooltip]="statusInfo.text"
                      *ngIf="statusInfo.text"
                    ></i>
                  </div>
                  <div class="file-actions">
                    <button
                      pButton
                      type="button"
                      icon="pi pi-download"
                      class="p-button-text p-button-sm"
                      (click)="downloadFile(file)"
                      [disabled]="!statusInfo.canDownload"
                      [pTooltip]="statusInfo.canDownload ? 'Download file' : (statusInfo.text || 'File not available for download')"
                    ></button>
                  </div>
                </ng-container>
              </div>
            </div>
          </div>
        </div>
    </div>
    </form>
  </div>
</p-dialog>

<p-dialog
  [(visible)]="readonlyMaxDialogVisible"
  [header]="readonlyExpanded ? readonlyMaxDialogHeaders[readonlyExpanded] : ''"
  [modal]="true"
  [dismissableMask]="true"
  [draggable]="false"
  [resizable]="false"
  [maximizable]="false"
  position="top"
  styleClass="change-description-max-dialog"
  [contentStyle]="readonlyMaxDialogContentStyle"
>
  <ng-container [ngSwitch]="readonlyExpanded">
    <div
      *ngSwitchCase="'changeDescription'"
      class="change-description-display change-description-display--maximized"
      [innerHTML]="rcaForm.get('changeDescription')?.value"
    ></div>
    <div
      *ngSwitchCase="'impactAssessJustify'"
      class="change-description-display change-description-display--maximized impact-assess-plain"
    >{{ rcaForm.get('impactAssessJustify')?.value }}</div>
    <div
      *ngSwitchCase="'timeProductNotify'"
      class="change-description-display change-description-display--maximized impact-assess-plain"
    >{{ rcaForm.get('timeProductNotify')?.value }}</div>
  </ng-container>
</p-dialog>

<ng-template #readonlyMaximizeToolbar let-field="field" let-ariaLabel="ariaLabel">
  <div class="readonly-maximize-toolbar">
    <button
      type="button"
      pButton
      class="change-description-maximize-btn p-button-text p-button-rounded p-button-sm"
      icon="pi pi-window-maximize"
      pTooltip="Open maximized"
      tooltipPosition="left"
      (click)="openReadonlyFullscreen(field)"
      [attr.aria-label]="ariaLabel"
    ></button>
  </div>
</ng-template>